import { TextInput } from '@mantine/core';
import { SelectableCard } from '@uag/react-core';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';

import { OnboardingRequestDetailModel } from 'api/models';
import { Card } from '../Card';
import existingCustomer from './assets/existingCustomer.svg';
import newCustomer from './assets/newCustomer.svg';
import { CustomerNumberField } from './CustomerNumberField';
import { NewCustomerCountryField } from './NewCustomerCountryField';

export const CustomerType = {
    New: 'New',
    Existing: 'Existing',
};

export type CustomerType = (typeof CustomerType)[keyof typeof CustomerType];

type Props = {
    onCustomerTypeChanged: (value: CustomerType) => void;
    customerType: CustomerType;
    onCustomerNumberChanged: (value: string, isCountrySupportedInDoka360: boolean) => void;
    onCompanyNameChanged: (value: string) => void;
    onCountryChanged: (countryId: string, isDoka360Available: boolean, countryCode: string) => void;
    onRequestAlreadyExists?: (existingRequest: OnboardingRequestDetailModel) => void;
    initialCustomerNumber?: string;
    initialCompanyName?: string;
    initialCountryId?: string;
};
export const CustomerSelection = ({
    customerType,
    onCustomerTypeChanged: handleCustomerTypeChanged,
    onCustomerNumberChanged: handleCustomerNumberChange,
    onCompanyNameChanged: handleCompanyNameChanged,
    onCountryChanged: handleCountryChanged,
    onRequestAlreadyExists: handleRequestAlreadyExists,
    initialCustomerNumber = '',
    initialCompanyName = '',
    initialCountryId = '',
}: Props) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-1">
            <h4 className="font-base font-default leading-base text-base font-normal">
                {t('customerType')}
                <span className="text-text-alert">*</span>
            </h4>
            <div className="flex flex-col gap-4">
                <SelectableCard.Group
                    className="w-full gap-6"
                    orientation="horizontal"
                    value={customerType}
                    wrap="nowrap"
                    onChange={handleCustomerTypeChanged}
                >
                    <Card
                        image={existingCustomer}
                        imageClassName="h-40"
                        text={t('existingCustomer')}
                        value={CustomerType.Existing}
                    />
                    <Card
                        className={clsx({ ['card-selected']: customerType === CustomerType.New })}
                        image={newCustomer}
                        imageClassName="h-40"
                        text={t('newCustomer')}
                        value={CustomerType.New}
                    />
                </SelectableCard.Group>
                {customerType === CustomerType.Existing && (
                    <>
                        <TextInput
                            classNames={{ section: 'border-l-0' }}
                            label={t('companyName')}
                            placeholder={t('customerNamePlaceholder')}
                            value={initialCompanyName}
                            required
                            onChange={(event) => handleCompanyNameChanged(event.currentTarget.value)}
                        />
                        <CustomerNumberField
                            initialCustomerNumber={initialCustomerNumber}
                            onCustomerNumberChanged={handleCustomerNumberChange}
                            onRequestAlreadyExists={handleRequestAlreadyExists}
                        />
                    </>
                )}
                {customerType === CustomerType.New && (
                    <NewCustomerCountryField
                        initialCountryId={initialCountryId}
                        onCountryChanged={handleCountryChanged}
                    />
                )}
            </div>
        </div>
    );
};
